import React, { useState, useEffect } from "react";
import { CaratClosedIcon } from "../controls/ThermalHeatMapSvgs";
import { AlertEvent, getHeatmapRowForAlert, getThresholdValue } from "../../hooks/useThermalAlerts";
import { formatTime } from "../../hooks/ThermalHeatMapHooks";
import type { HeatmapRow } from "../../hooks/ThermalHeatMapHooks";
import type { ThresholdConfig } from "./ThermalAlertThresholds";
import NewLoader from "../../../../../../../CommonComponents/NewLoader";
import AlertDescriptionRow from "./AlertDescriptionRow";

export interface ThermalAlertsPanelProps {
  alerts: AlertEvent[];
  isLoading?: boolean;
  defaultOpen?: boolean;
  onAlertClick?: (alert: AlertEvent) => void;
  heatmapRows?: HeatmapRow[];
  thresholds?: ThresholdConfig[];
}

const ThermalAlertsPanel: React.FC<ThermalAlertsPanelProps> = ({ 
  alerts, 
  isLoading, 
  defaultOpen, 
  onAlertClick,
  heatmapRows,
  thresholds,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedAlertKey, setExpandedAlertKey] = useState<string | null>(null);

  const hasAlerts = alerts.length > 0;
  useEffect(() => { if (hasAlerts) setIsOpen(true); }, [hasAlerts]);
  useEffect(() => { if (defaultOpen) setIsOpen(true); }, [defaultOpen]);

  const criticalCount = alerts.filter((a) => a.severity === "critical").length;
  const warningCount = alerts.filter((a) => a.severity === "warning").length;

  const handleAlertRowClick = (alert: AlertEvent) => {
    const isExpanded = expandedAlertKey === alert.key;
    setExpandedAlertKey(isExpanded ? null : alert.key);
    if (onAlertClick && !isExpanded) {
      onAlertClick(alert);
    }
  };

  const shouldShowDescription = (alert: AlertEvent): boolean => {
    return alert.face === "PX" || alert.face === "NX" || alert.face === "INT1" || alert.face === "INT2";
  };

  return (
    <div className="thm-alerts-panel">
      <div className="thm-alerts-panel-header" onClick={() => setIsOpen((v) => !v)}>
        <button className="thm-alerts-caret-btn" type="button">
          <span style={{ display: "flex", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 300ms ease" }}>
            <CaratClosedIcon active={isOpen} />
          </span>
        </button>
        <span className="thm-alerts-panel-title">ALERTS</span>
        <div className="thm-alerts-header-badges">
          {criticalCount > 0 && <span className="thm-alerts-badge thm-alerts-badge--critical">{criticalCount} Critical</span>}
          {warningCount > 0 && <span className="thm-alerts-badge thm-alerts-badge--warning">{warningCount} Warning</span>}
          {alerts.length === 0 && <span className="thm-alerts-badge thm-alerts-badge--ok">OK</span>}
        </div>
      </div>

      {isOpen && (
        <div className="thm-alerts-body">
          {isLoading ? (
            <NewLoader canvasHeight="60px" canvasWidth="100%" loaderpage={undefined} />
          ) : alerts.length === 0 ? (
            <div className="thm-alerts-empty">No threshold violations detected</div>
          ) : (
            <div className="thm-alerts-table-wrap">
              <table className="thm-alerts-table">
                <thead>
                  <tr>
                    <th>Severity</th>
                    <th>Face</th>
                    <th>Threshold</th>
                    <th>Breach</th>
                    <th>Temperature</th>
                    <th>Margin</th>
                    <th>Time Range</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert) => {
                    const isExpanded = expandedAlertKey === alert.key;
                    const showDescription = shouldShowDescription(alert);
                    return (
                      <React.Fragment key={alert.key}>
                        <tr
                          className={`thm-alert-tr thm-alert-tr--${alert.severity} ${isExpanded ? "thm-alert-tr--expanded" : ""}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleAlertRowClick(alert)}
                        >
                          <td>
                            <span className={`thm-alert-severity-badge thm-alert-severity-badge--${alert.severity}`}>
                              {alert.severity === "critical" ? "CRIT" : "WARN"}
                            </span>
                          </td>
                          <td>{alert.faceLabel}</td>
                          <td>{alert.thresholdName}</td>
                          <td className={`thm-alert-breach thm-alert-breach--${alert.breach}`}>
                            {alert.breach === "high" ? "▲ High" : "▼ Low"}
                          </td>
                          <td className={`thm-alert-temp thm-alert-temp--${alert.breach}`}>
                            {alert.peakTemp.toFixed(1)}°C
                          </td>
                          <td className="thm-alert-margin">{alert.margin.toFixed(2)}°C</td>
                          <td className="thm-alert-time">{formatTime(alert.timeSec)} - {formatTime(alert.endTimeSec)}</td>
                        </tr>
                        {isExpanded && showDescription && (
                          <AlertDescriptionRow
                            alert={alert}
                            heatmapRow={heatmapRows ? getHeatmapRowForAlert(alert, heatmapRows) : undefined}
                            thresholdValue={thresholds ? getThresholdValue(alert, thresholds) : 0}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(ThermalAlertsPanel);
